import { request } from '@umijs/max';

export interface AccountPlanItem {
  id: number;
  name: string;
  points: number;
  price: number;
  tag: string;
  coverUrl: string;
}

export interface AccountCenterResponse {
  planCoverUrl: string;
  plans: AccountPlanItem[];
}

export interface AccountPlansPagedResponse {
  list: AccountPlanItem[];
  total: number;
  current: number;
  pageSize: number;
}

export interface ApiSuccessResponse<T> {
  success: true;
  data: T;
}

async function getApiData<T>(url: string, params?: Record<string, any>): Promise<{ data: T }> {
  const res = await request<any>(url, {
    method: 'GET',
    params,
    baseURL: '',
  } as any);
  // 统一返回结构：{ success: true, data: payload }
  // Umi useRequest 默认 formatResult = (result) => result?.data
  // 因此这里返回 { data: payload }，让 useRequest 的 data 直接等于 payload
  const body = (res?.data ?? res) as any;
  if (body?.success === true && 'data' in body) {
    return { data: body.data as T };
  }
  return { data: body as T };
}

export async function getGenerationRecords(scope: string, page: number = 1, pageSize: number = 10) {
  const { data } = await getApiData<{ list: any[]; total: number; current: number; pageSize: number }>(
    '/api/qn/generation-records',
    { scope, current: page, pageSize },
  );

  const list = Array.isArray((data as any)?.list) ? (data as any).list : [];
  const mapped: GenRecordItem[] = list.map((r: any) => {
    const statusRaw = String(r?.status ?? '').toUpperCase();
    const status: GenRecordStatus =
      statusRaw === 'SUCCESS'
        ? 'success'
        : statusRaw === 'FAIL'
          ? 'failed'
          : statusRaw === 'PROCESSING'
            ? 'running'
            : 'queued';

    const url = String(r?.resultUrl ?? '');
    const kind: 'video' | 'image' = scope === 'nano-banana-pro' || scope === 'gpt-image' ? 'image' : 'video';
    const prompt = String(r?.prompt ?? '');
    const title =
      prompt ? prompt.slice(0, 18) + (prompt.length > 18 ? '…' : '') : `任务 ${r?.batchId ?? r?.requestId ?? r?.id}`;

    const succ = typeof r?.successCount === 'number' ? r.successCount : 0;
    const fail = typeof r?.failCount === 'number' ? r.failCount : 0;
    const tot = typeof r?.totalCount === 'number' ? r.totalCount : undefined;
    const deductedPoints = typeof r?.deductedPoints === 'number' ? r.deductedPoints : undefined;
    const pointsPart = typeof deductedPoints === 'number' ? `${deductedPoints} 积分` : undefined;
    const countPart =
      typeof tot === 'number' ? `${succ}/${tot} 成功${fail ? ` · ${fail} 失败` : ''}` : undefined;
    const pointsText =
      pointsPart && countPart ? `${pointsPart} · ${countPart}` : pointsPart || countPart || undefined;

    return {
      id: String(r?.batchId ?? r?.requestId ?? r?.id ?? ''),
      title,
      prompt,
      modelLabel: String(r?.scope ?? scope),
      createdAtText: String(r?.createdAt ?? r?.createdAtText ?? ''),
      status,
      kind,
      videoUrl: kind === 'video' ? url || undefined : undefined,
      coverUrl: kind === 'image' ? url || undefined : undefined,
      pointsText,
      jobIdText: String(r?.batchId ?? r?.requestId ?? ''),
      createdAtFullText: String(r?.createdAt ?? ''),
    };
  });

  return { data: { ...data, list: mapped } };
}

export async function getAccountCenter() {
  return getApiData<AccountCenterResponse>('/api/qn/account-center');
}

export async function getAccountPlansPaged(page: number = 1, pageSize: number = 6) {
  return getApiData<AccountPlansPagedResponse>('/api/qn/account-center/plans', {
    current: page,
    pageSize,
  });
}

export async function getAssetsMock() {
  return getApiData<any>('/api/qn/assets', {});
}

export async function getAssetsPaged(params: {
  page: number;
  pageSize: number;
  typeKey: string;
  statusKey: string;
}) {
  const { data } = await getApiData<any>('/api/qn/assets', {
    current: params.page,
    pageSize: params.pageSize,
    typeKey: params.typeKey,
    statusKey: params.statusKey,
  });
  const list = Array.isArray((data as any)?.list) ? (data as any).list : [];
  const mapped = list.map((it: any) => {
    const typeKeyRaw = String(it?.typeKey ?? '');
    const mediaKindRaw = String(it?.mediaKind ?? '').toLowerCase();
    const typeKey =
      typeKeyRaw === 'img-gen' || typeKeyRaw === 'video-gen'
        ? typeKeyRaw
        : mediaKindRaw === 'image'
          ? 'img-gen'
          : 'video-gen';
    const statusKeyRaw = String(it?.statusKey ?? '').toLowerCase();
    const statusKey =
      statusKeyRaw === 'running' || statusKeyRaw === 'done' || statusKeyRaw === 'failed'
        ? statusKeyRaw
        : 'done';
    const assetUrl = String(it?.assetUrl ?? it?.resultUrl ?? it?.thumbUrl ?? '');
    const thumbUrl = String(it?.thumbUrl ?? it?.assetUrl ?? it?.resultUrl ?? '');
    const filename = String(it?.filename ?? it?.title ?? `任务 ${it?.batchId ?? it?.id ?? ''}`);
    return {
      id: String(it?.id ?? it?.batchId ?? ''),
      batchId: String(it?.batchId ?? it?.id ?? ''),
      typeKey,
      statusKey,
      mediaKind: typeKey === 'img-gen' ? 'image' : 'video',
      filename,
      sizeText: String(it?.sizeText ?? ''),
      thumbUrl,
      assetUrl,
    };
  });
  return { data: { ...data, list: mapped } };
}

export interface PromptCategoryItem {
  key: string;
  label: string;
}

export interface PromptCardItem {
  id: number;
  title: string;
  coverUrl: string;
  categoryKey: string;
  badge?: string;
  size: 'xl' | 'lg' | 'sm';
  models?: string[];
  usedCount?: number;
  promptText?: string;
  tags?: string[];
}

export async function getPromptCategories() {
  return getApiData<PromptCategoryItem[]>('/api/qn/prompts/categories');
}

export async function getPromptCardsPaged(params: {
  categoryKey?: string;
  keyword?: string;
  page: number;
  pageSize: number;
}) {
  return getApiData<{ list: PromptCardItem[]; total: number; current: number; pageSize: number }>(
    '/api/qn/prompts/cards',
    {
      categoryKey: params.categoryKey,
      keyword: params.keyword,
      current: params.page,
      pageSize: params.pageSize,
    },
  );
}

export async function getPromptCardDetail(id: number) {
  return getApiData<PromptCardItem>(`/api/qn/prompts/cards/${id}`);
}

export type GenRecordStatus = 'queued' | 'running' | 'success' | 'failed';

export interface GenRecordItem {
  id: string;
  title: string;
  prompt: string;
  modelLabel: string;
  createdAtText: string;
  status: GenRecordStatus;
  kind?: 'video' | 'image';
  coverUrl?: string;
  videoUrl?: string;
  pointsText?: string;
  jobIdText?: string;
  createdAtFullText?: string;
}

export async function createGenerationTask(params: {
  scope: string;
  prompt: string;
  costPoints?: number;
  count?: number;
  modelKey?: string;
  aspectRatio?: string;
  imageSize?: string;
  urls?: string[];
}) {
  const res = await request<any>('/api/qn/generation-tasks', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: params,
    baseURL: '',
  } as any);
  const body = (res?.data ?? res) as any;
  if (body?.success === true && 'data' in body) {
    return { data: body.data as any };
  }
  return { data: body as any };
}

export async function queryGenerationTask(requestId: string) {
  const res = await request<any>(`/api/qn/generation-tasks/${requestId}`, {
    method: 'GET',
    baseURL: '',
  } as any);
  const body = (res?.data ?? res) as any;
  if (body?.success === true && 'data' in body) {
    return { data: body.data as any };
  }
  return { data: body as any };
}

export async function deleteGenerationRecord(requestId: string) {
  const res = await request<any>(`/api/qn/generation-records/${encodeURIComponent(requestId)}`, {
    method: 'DELETE',
    baseURL: '',
  } as any);
  const body = (res?.data ?? res) as any;
  if (body?.success === false) {
    throw new Error(String(body?.errorMessage || body?.message || '删除失败'));
  }
  const ok = body?.success === true ? body?.data : body;
  if (ok !== true) {
    throw new Error('仅失败任务可删除');
  }
  return { data: true };
}

export async function getGenerationBatchDetail(batchId: string) {
  return getApiData<any[]>(`/api/qn/generation-records/batches/${encodeURIComponent(batchId)}`);
}
